'use client';
import React, { useCallback, useRef, useState } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { FormattedMessage } from 'react-intl';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { textBlue } from '@osmosis/tailwind-ui/styles/text';
import { useSkipLinkContext } from '../context/SkipLinkContext';

/**
 * Dedicated skip destination for the Osmosis AI (OAI) assistant. It is rendered
 * only when OAI is available on the page (AIPanelProvider registers this via
 * SkipLinkContext). The priority list is resolved to the first *visible* target
 * on activation, which encodes the required behavior:
 *  - open   -> `ai-panel` (the panel region) is visible -> land at the start of
 *             the assistant.
 *  - closed -> `ai-panel` is hidden, so focus falls to whichever open control
 *             is present: the collapsed-rail expand button
 *             (`ai-panel-expand-btn`) or the standard "Osmosis AI" button
 *             (`osmosis-ai-btn`).
 */
const OAI_SKIP_LINK = {
  linkText: 'the Osmosis AI assistant',
  linkUrl: ['ai-panel', 'ai-panel-expand-btn', 'osmosis-ai-btn'],
};

/**
 * A target is only a valid skip destination when it is actually visible. A
 * `display:none`/`hidden` element - e.g. the OAI panel while the user has it
 * collapsed or closed - cannot receive focus, so skipping to it would land the
 * user nowhere. Treat it as absent and fall through to the next candidate.
 */
const isElementVisible = (element: HTMLElement): boolean => {
  if (element.hidden) {
    return false;
  }
  const { display, visibility } = window.getComputedStyle(element);
  return display !== 'none' && visibility !== 'hidden';
};

/**
 * First *visible* element matching the data-testid, or null. Visibility is
 * checked so a hidden candidate (such as a closed OAI panel) is skipped in
 * favor of the next id in the priority list.
 */
const findVisibleElementByTestId = (testId: string): HTMLElement | null => {
  const matches = document.querySelectorAll<HTMLElement>(
    `[data-testid="${testId}"]`,
  );
  for (let i = 0; i < matches.length; i++) {
    const element = matches[i];
    if (isElementVisible(element)) {
      return element;
    }
  }
  return null;
};

type SkipLinkAnchorProps = {
  linkText: string;
  linkUrl: string[];
  testId: string;
  onShow: () => void;
  onHide: (event: React.FocusEvent) => void;
};

/**
 * A single skip-link anchor. Resolves its destination from a priority list of
 * data-testids to the first *visible* one at activation time, and moves focus
 * there without recentring the page. Visibility is owned by the parent
 * SkipLink group (not per-anchor) so that focusing any link reveals the whole
 * skip-links menu; the anchor just reports focus in/out via onShow/onHide.
 */
const SkipLinkAnchor = ({
  linkText,
  linkUrl,
  testId,
  onShow,
  onHide,
}: SkipLinkAnchorProps) => {
  const performSkip = useCallback(() => {
    const targetElement = linkUrl.reduce<HTMLElement | null>(
      (found, id) => found ?? findVisibleElementByTestId(id),
      null,
    );

    if (!targetElement) {
      return;
    }

    const originalTabIndex = targetElement.getAttribute('tabIndex');
    if (!targetElement.hasAttribute('tabIndex')) {
      targetElement.setAttribute('tabIndex', '-1');
    }
    // Move focus without letting the browser recentre the page, then only
    // scroll if the target isn't already on screen (block: 'nearest' is a
    // no-op when it's visible - e.g. the main-content landmark at the top).
    targetElement.focus({ preventScroll: true });
    targetElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    // Restore the original tabIndex after a short delay
    setTimeout(() => {
      if (originalTabIndex === null) {
        targetElement.removeAttribute('tabIndex');
      } else {
        targetElement.setAttribute('tabIndex', originalTabIndex);
      }
    }, 0);
  }, [linkUrl]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      performSkip();
    },
    [performSkip],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }

      e.preventDefault();
      performSkip();
    },
    [performSkip],
  );

  return (
    <a
      href={`#${linkUrl[0]}`}
      className={`skip-link p-2 rounded-md ${textBlue} font-bold underline whitespace-nowrap`}
      onFocus={onShow}
      onBlur={onHide}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={testId}
    >
      <FormattedMessage
        id="sidenav.skip.link"
        defaultMessage="Skip to {linkText}"
        values={{ linkText }}
      />
    </a>
  );
};

/**
 * Global skip links used for accessibility. Rendered exactly once, as the
 * first focusable elements in PageContent (ahead of the header), so they are
 * always first in native tab order rather than relying on a "first Tab
 * press" heuristic.
 *
 * The first anchor reads its destination from SkipLinkContext, which pages
 * register via the useSkipLinkTarget hook (falling back to a default
 * "main content" target when a page registers nothing).
 *
 * The second anchor is a dedicated skip link to the Osmosis AI assistant. It
 * is rendered only on pages that actually mount OAI and sends the user to the
 * open panel when expanded, or to the control that opens it when closed - so
 * there is never an OAI skip link on a page without OAI, and it never
 * dead-ends. Pages whose OAI entry point is the same element as their
 * main-content target (e.g. the dashboard, where both are the unified search
 * input) suppress this second link so only a single link renders.
 *
 * Visibility is owned here, at the group level: focusing any one link reveals
 * the whole menu (both links at once, stacked in a column) and it stays
 * visible while focus moves between the links, hiding only once focus leaves
 * the group entirely. A single `fixed` container keeps the menu pinned to the
 * top-left of the viewport above all page content, so it is immediately
 * visible on every page regardless of scroll position or layout.
 */
const SkipLink = () => {
  const {
    target: { linkText, linkUrl },
    isOaiAvailable,
    oaiOverride,
  } = useSkipLinkContext();

  const showOaiLink = isOaiAvailable && oaiOverride !== 'suppressed';
  const oaiTarget =
    oaiOverride && oaiOverride !== 'suppressed' ? oaiOverride : OAI_SKIP_LINK;

  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => setIsVisible(true), []);

  // Hide only when focus leaves the group; moving between the grouped links
  // keeps the menu open (relatedTarget is the sibling link).
  const hide = useCallback((event: React.FocusEvent) => {
    if (containerRef.current?.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setIsVisible(false);
  }, []);

  return (
    <Box
      ref={containerRef}
      className={`skip-links ${bg0} border border-black rounded-md fixed p-2 top-12 left-24 flex flex-col items-start gap-2 ${
        isVisible ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
      style={{
        opacity: isVisible ? 1 : 0,
        zIndex: 99999,
        ...(isVisible ? {} : { top: -1000, left: -1000 }),
      }}
    >
      <SkipLinkAnchor
        linkText={linkText}
        linkUrl={linkUrl}
        testId="skip-link"
        onShow={show}
        onHide={hide}
      />
      {showOaiLink && (
        <SkipLinkAnchor
          linkText={oaiTarget.linkText}
          linkUrl={oaiTarget.linkUrl}
          testId="skip-link-oai"
          onShow={show}
          onHide={hide}
        />
      )}
    </Box>
  );
};

export default SkipLink;
